/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createCsvExport,
  getCsvExportStatus,
} from "../api/csvExportService";
import { useNotificationContext } from "./notificationContext";
import { getMutationErrorMessage } from "../hooks/mutations/utils/mutationErrorMessage";
import { Query_Keys } from "../lib/queryKeys";

// Poll cadence backs off with elapsed time: snappy while a small export is likely
// to finish, sparse once it is clearly a long one. A large orders export can run
// several minutes, so the cap is 15 minutes — reached in ~110 requests, fewer than
// a flat 2s cadence spent in the first 4.
const POLL_STAGES = [
  { throughMs: 30_000, intervalMs: 2_000 },
  { throughMs: 120_000, intervalMs: 5_000 },
];
const POLL_MAX_INTERVAL_MS = 10_000;
const MAX_POLL_DURATION_MS = 15 * 60 * 1000;

export const pollIntervalFor = (elapsedMs) =>
  POLL_STAGES.find((stage) => elapsedMs < stage.throughMs)?.intervalMs ?? POLL_MAX_INTERVAL_MS;

// Tolerate a few consecutive status blips before failing: the background job may
// still be running, so one transient error should not discard the whole export.
const MAX_POLL_ERRORS = 3;

const READY = "ready";
const FAILED = "failed";
// Internal sentinel: a caught poll error is surfaced as data (not a rejected
// query) so refetchInterval keeps the poll alive and we can count the streak.
const POLL_ERROR = "__poll_error__";

// Stream the finished file straight from storage. We deliberately do NOT
// fetch()+blob() the url: the browser downloads directly (O(1) tab memory, no
// buffering of large files) and cross-origin storage urls would fail CORS.
// For cross-origin urls the browser ignores `download` and honors the server's
// Content-Disposition filename; `download` only applies for same-origin (dev).
const triggerDownload = (url, fileName) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  if (fileName) anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

/**
 * Drives a single background CSV export lifecycle: POST to start, poll the
 * status until ready/failed/timeout, then auto-download. Mounted by the
 * provider (above the router) so the poll survives page navigation. Calls
 * `onDone` exactly once when it reaches a terminal state so the provider can
 * drop it from the active registry.
 */
const CsvExportPoller = ({ report, params, fileName, label, onDone }) => {
  const showNotification = useNotificationContext();
  const downloadSuccessMessage = label
    ? `${label} CSV downloaded successfully`
    : "CSV downloaded successfully";
  const [exportId, setExportId] = useState(null);

  // Refs guard against races across the async gap between deciding an outcome
  // and React re-rendering: `finishedRef` ensures a single terminal transition,
  // `downloadedRef` a single download.
  const finishedRef = useRef(false);
  const downloadedRef = useRef(false);
  const startedAtRef = useRef(null);
  const errorCountRef = useRef(0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // Time since the first poll. refetchInterval is evaluated before the effect below,
  // so it can only ever see a smaller elapsed value — the poll never stops without
  // the effect also recognising the timeout.
  const elapsedMs = () =>
    startedAtRef.current === null ? 0 : Date.now() - startedAtRef.current;

  const finish = useCallback((notify) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    startedAtRef.current = null;
    errorCountRef.current = 0;
    setExportId(null);
    notify();
    onDoneRef.current?.(report);
  }, [report]);

  const startMutation = useMutation({
    mutationFn: (mutationParams) => createCsvExport(mutationParams),
    onSuccess: (data) => {
      if (!data?.id) {
        finish(() => showNotification("Failed to start CSV export", "error"));
        return;
      }
      setExportId(data.id);
    },
    onError: (error) => {
      finish(() =>
        showNotification(getMutationErrorMessage(error, "Failed to start CSV export"), "error")
      );
    },
  });

  const startedRef = useRef(false);
  const { mutate: startMutate } = startMutation;
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startMutate({ report, ...(params || {}) });
    // Deps intentionally empty: a poller starts its export exactly once per mount.
  }, []);

  const pollQuery = useQuery({
    queryKey: Query_Keys.csv_export_status(exportId),
    // Catch errors into a sentinel instead of rejecting: a rejected query stops
    // refetchInterval, but a transient blip should keep polling (the effect
    // fails the export only after MAX_POLL_ERRORS consecutive blips). The shared
    // axios instance already retries network/gateway errors before this runs.
    queryFn: () => {
      if (startedAtRef.current === null) startedAtRef.current = Date.now();
      return getCsvExportStatus(exportId).catch(() => ({ status: POLL_ERROR }));
    },
    enabled: Boolean(exportId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === READY || status === FAILED) return false;
      const elapsed = elapsedMs();
      if (elapsed >= MAX_POLL_DURATION_MS) return false;
      return pollIntervalFor(elapsed);
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    retry: false,
  });

  // React to each poll result: download on ready, toast on failure/timeout.
  // Depends on dataUpdatedAt so it re-runs on every poll even when react-query's
  // structural sharing returns an identical data reference.
  const { data: pollData, dataUpdatedAt } = pollQuery;
  useEffect(() => {
    if (!exportId || finishedRef.current) return;

    if (pollData?.status === READY) {
      if (downloadedRef.current) return;
      if (!pollData.url) {
        finish(() => showNotification("Export finished but no file was produced", "error"));
        return;
      }
      downloadedRef.current = true;
      triggerDownload(pollData.url, fileName);
      finish(() => showNotification(downloadSuccessMessage, "success"));
    } else if (pollData?.status === FAILED) {
      finish(() => showNotification(pollData.error || "CSV export failed", "error"));
    } else if (pollData?.status === POLL_ERROR) {
      errorCountRef.current += 1;
      if (errorCountRef.current >= MAX_POLL_ERRORS) {
        finish(() => showNotification("CSV export failed", "error"));
      }
    } else if (pollData) {
      errorCountRef.current = 0;
      if (elapsedMs() >= MAX_POLL_DURATION_MS) {
        finish(() =>
          showNotification("Export is taking longer than expected. Please try again.", "error")
        );
      }
    }
  }, [exportId, pollData, dataUpdatedAt]);

  return null;
};

const CsvExportContext = createContext(null);

export const useCsvExportContext = () => {
  const context = useContext(CsvExportContext);
  if (!context) {
    throw new Error(
      "useCsvExportContext must be used within a CsvExportProvider"
    );
  }
  return context;
};

/**
 * App-level provider (mounted above the router) that owns background CSV export
 * lifecycles so they survive page navigation. Holds a registry keyed by report;
 * each active export is driven by a mounted CsvExportPoller. Consumers read
 * `isExporting(report)` for loading state and call `startExport` to begin one.
 */
export const CsvExportProvider = ({ children }) => {
  // Registry of in-flight exports keyed by report. `runId` is only used as a
  // React key so a fresh export of the same report (after a prior one finished)
  // remounts a clean poller.
  const [activeExports, setActiveExports] = useState({});
  const runIdRef = useRef(0);

  const removeExport = useCallback((report) => {
    setActiveExports((prev) => {
      if (!(report in prev)) return prev;
      const next = { ...prev };
      delete next[report];
      return next;
    });
  }, []);

  const startExport = useCallback(({ report, params, fileName, label } = {}) => {
    if (!report) return;
    setActiveExports((prev) => {
      if (prev[report]) return prev;
      runIdRef.current += 1;
      return {
        ...prev,
        [report]: { params, fileName, label, runId: runIdRef.current },
      };
    });
  }, []);

  const isExporting = useCallback(
    (report) => Boolean(activeExports[report]),
    [activeExports]
  );

  const value = useMemo(
    () => ({ startExport, isExporting }),
    [startExport, isExporting]
  );

  return (
    <CsvExportContext.Provider value={value}>
      {children}
      {Object.entries(activeExports).map(([report, request]) => (
        <CsvExportPoller
          key={`${report}:${request.runId}`}
          report={report}
          params={request.params}
          fileName={request.fileName}
          label={request.label}
          onDone={removeExport}
        />
      ))}
    </CsvExportContext.Provider>
  );
};
